// Phase SC-G1 GAP 4 — Sport badge config.
//
// Pure helper that maps a sport key (from sc_slots.sport) to a label,
// hex color, and emoji per the Multi-Sport Amendment v1.0 Section 02.
//
// AC-4.3: default fallback to football when sport is null or
// unrecognised. Case-insensitive normalisation.

export type SportKey =
  | "football"
  | "nba"
  | "tennis"
  | "nfl"
  | "mlb"
  | "nhl"
  | "rugby";

export interface SportBadgeConfig {
  /** Uppercase label shown inside the badge. */
  label: string;
  /** Hex background color per Amendment v1.0 Table 12. */
  color: string;
  /** Single-codepoint emoji shown left of the label. */
  emoji: string;
  /** Normalised sport key for class names and analytics. */
  key: SportKey;
}

export const SPORT_CONFIG: Record<SportKey, SportBadgeConfig> = {
  football: { key: "football", label: "FOOTBALL", color: "#1A3D4D", emoji: "⚽" },
  nba: { key: "nba", label: "NBA", color: "#1A2E4D", emoji: "🏀" },
  tennis: { key: "tennis", label: "TENNIS", color: "#1A4D2E", emoji: "🎾" },
  nfl: { key: "nfl", label: "NFL", color: "#4D3A1A", emoji: "🏈" },
  mlb: { key: "mlb", label: "MLB", color: "#0D1A3D", emoji: "⚾" },
  nhl: { key: "nhl", label: "NHL", color: "#2A2A3D", emoji: "🏒" },
  rugby: { key: "rugby", label: "RUGBY", color: "#1A3D1A", emoji: "🏉" },
};

const KNOWN_SPORTS: ReadonlySet<SportKey> = new Set(
  Object.keys(SPORT_CONFIG) as SportKey[],
);

/**
 * Returns the SportBadgeConfig for a given sport key. Case-insensitive.
 * AC-4.3: null, undefined, empty, or unknown -> football fallback.
 */
export function getSportBadge(sport: string | null | undefined): SportBadgeConfig {
  if (!sport) return SPORT_CONFIG.football;
  const normalised = sport.trim().toLowerCase();
  if (normalised.length === 0) return SPORT_CONFIG.football;
  if (KNOWN_SPORTS.has(normalised as SportKey)) {
    return SPORT_CONFIG[normalised as SportKey];
  }
  return SPORT_CONFIG.football;
}
